import React, { createContext, useContext, useEffect, useState, useRef } from "react";
import { endpoints } from "./api";
import { supabase } from "./supabase";

const AuthContext = createContext(null);

// Single-source auth: we rely entirely on supabase.auth.onAuthStateChange.
// On subscribe, Supabase always fires INITIAL_SESSION immediately (with the
// restored session OR null) - no need for a parallel getSession() call that
// can race the listener and cause double renders.
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDemo, setIsDemo] = useState(false);
  const initRef = useRef(false);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const { data } = await endpoints.me();
        setUser(data);
        setIsDemo(false);
        return true;
      } catch (err) {
        const status = err?.response?.status;
        if (status === 401 || status === 404) {
          try {
            await endpoints.oauthComplete({});
            const { data } = await endpoints.me();
            setUser(data);
            setIsDemo(false);
            return true;
          } catch {}
        }
        return false;
      }
    };

    const enterDemo = async () => {
      try {
        const { data } = await endpoints.demoMe();
        setUser(data);
        setIsDemo(true);
      } catch {
        setUser(null);
        setIsDemo(false);
      }
    };

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      // CRITICAL: never call Supabase APIs synchronously inside this listener
      // (it holds an internal lock and the axios interceptor calls getSession,
      // which deadlocks the whole chain). Defer the actual work to the next tick.
      setTimeout(async () => {
        if (event === "SIGNED_OUT") {
          await enterDemo();
          setLoading(false);
          return;
        }
        if (session) {
          const ok = await loadProfile();
          if (!ok) await enterDemo();
        } else if (!initRef.current) {
          await enterDemo();
        }
        initRef.current = true;
        setLoading(false);
      }, 0);
    });

    return () => sub?.subscription?.unsubscribe?.();
  }, []);

  const refresh = async () => {
    try {
      const { data } = await endpoints.me();
      setUser(data);
      setIsDemo(false);
      return true;
    } catch {
      return false;
    }
  };

  const logout = async () => {
    try { await supabase.auth.signOut(); } catch {}
    try { await endpoints.logout(); } catch {}
    setUser(null);
    setIsDemo(false);
    window.location.href = "/login";
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout, refresh, isDemo }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
